import Logout from "@components/Logout";
import { ModernSelect } from "@components/Select";
import ROUTES from "@constants/routing";
import { ClientContext } from "@context/Client/ClientContext";
import { changeLanguage } from "@i18n/i18n";
import {
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import { SelectChangeEvent } from "@mui/material/Select";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppBarMenu from "./AppBarMenu";

export interface ResponsiveAppBarProps {
  tabs: JSX.Element[];
}

const ResponsiveAppBar = ({ tabs }: ResponsiveAppBarProps) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { currentClient } = React.useContext(ClientContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize with the current language of the system i18n and persist in localStorage
  const [selectedLng, setSelectedLng] = React.useState(() => {
    const storedLang = localStorage.getItem("selectedLanguage");
    if (storedLang) {
      return storedLang;
    }
    return i18n.language?.toUpperCase() || "EN";
  });

  // Sync with the current language when the component is mounted or changed
  React.useEffect(() => {
    const currentLang = i18n.language?.toUpperCase() || "EN";
    if (currentLang !== selectedLng) {
      setSelectedLng(currentLang);
    }
  }, [i18n.language, selectedLng]);

  // Listen to language changes of i18n (including those from Keycloak)
  React.useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const upperLng = lng.toUpperCase();
      setSelectedLng(upperLng);
      localStorage.setItem("selectedLanguage", upperLng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  const handleChangeLanguage = (event: SelectChangeEvent) => {
    const lngSelected = event.target.value as string;
    setSelectedLng(lngSelected);
    changeLanguage(lngSelected.toLocaleLowerCase());
    // Persist the preference in localStorage
    localStorage.setItem("selectedLanguage", lngSelected);
  };

  const showMenuIcon = React.useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: theme.palette.primary.main
      }}
    >
      <Grid
        container
        sx={{
          height: theme.customLayoutHeight.appNavBarHeight,
          px: 4,
          width: "100%",

          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "1rem"
          }}
        >
          {showMenuIcon && (
            <Box
              sx={{
                color: "white"
              }}
            >
              <AppBarMenu />
            </Box>
          )}
          <Link to={ROUTES.HOME}>
            <Box className="flex items-center">
              <Box>
                <img
                  src={`data:image/png;base64,${currentClient.client.logo_path}`}
                  alt={`${currentClient.client.name} logo`}
                  className="group-hover:fill-white max-w-20 max-h-12 object-contain bg-white rounded-md"
                />
              </Box>
              <Typography
                variant="h5"
                component="div"
                className="pl-2"
                sx={{ color: "white" }}
              >
                Waves
              </Typography>
            </Box>
          </Link>
        </Box>
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            gap: 1,
            alignItems: "center"
          }}
        >
          {tabs}
          <Divider
            sx={{
              my: 2
            }}
            orientation="vertical"
            color="white"
            variant="middle"
            flexItem
          />
          <Box sx={{ minWidth: 120, marginLeft: "10px" }}>
            <ModernSelect
              options={["ES", "EN"]}
              value={selectedLng}
              onChange={handleChangeLanguage}
              id="language-select"
              colorTheme="dark"
              minWidth={120}
              variant="outlined"
            />
          </Box>
          <Logout />
        </Box>
      </Grid>
    </Box>
  );
};
export default ResponsiveAppBar;
